import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Users, 
  Building2, 
  FileText, 
  Clock, 
  Shield, 
  ArrowRight,
  Plane,
  Briefcase,
  GraduationCap,
  Home,
  Globe
} from 'lucide-react';
import Sidebar from '../../components/layout/Sidebar';
export default function ImmigrationServicesDetail() {
  return (
    <div className="max-w-7xl mx-auto px-4 flex gap-8">
    <Sidebar />
        <div className="min-h-screen bg-gradient-to-br from-slate-200 via-blue-50 to-indigo-200">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-20 overflow-hidden">
            <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
            <div className="relative max-w-7xl mx-auto px-4">
            <div className="max-w-3xl">
                <div className="inline-flex items-center px-4 py-2 bg-green-500/20 backdrop-blur-sm rounded-full text-green-300 text-sm font-medium mb-6 border border-green-500/30">
                <CheckCircle className="w-4 h-4 mr-2" />
                Fast, Reliable & Compliant Solutions
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Immigration, Visa & Work Permit Services
                </h1>
                <p className="text-xl text-slate-300 leading-relaxed">
                Navigate Kenya's immigration landscape with confidence. We simplify complex procedures and ensure full compliance with government regulations.
                </p>
            </div>
            </div>
        </section>

        {/* Overview */}
        <section className="py-16">
            <div className="max-w-7xl mx-auto px-4">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
                <div className="flex items-start gap-4">
                <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                    <Globe className="w-8 h-8" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold mb-4">Why Choose Northstart Systems?</h2>
                    <p className="text-lg text-blue-50 leading-relaxed">
                    Northstart Systems provides reliable and efficient support for individuals 
                    and organizations navigating Kenya's immigration and labor requirements. 
                    Our services are designed to simplify complex procedures and ensure compliance 
                    with all government regulations.
                    </p>
                </div>
                </div>
            </div>
            </div>
        </section>

        {/* Core Services */}
        <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Comprehensive Services</h2>
                <p className="text-xl text-gray-600">End-to-end immigration solutions tailored to your needs</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Visa Processing */}
                <div className="group bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100 rounded-2xl p-6 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
                <div className="bg-blue-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Plane className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Visa Processing
                </h3>
                <p className="text-gray-600 mb-4">
                    Comprehensive visa services for all types of visits and durations.
                </p>
                <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Short-term visa applications (tourist, business, transit)</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Long-term visa processing</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Visa extensions and renewals</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Multiple entry visa facilitation</span>
                    </li>
                </ul>
                </div>

                {/* Work Permits */}
                <div className="group bg-gradient-to-br from-white to-indigo-50 border-2 border-indigo-100 rounded-2xl p-6 hover:border-indigo-300 hover:shadow-xl transition-all duration-300">
                <div className="bg-indigo-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Briefcase className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Work Permit Applications
                </h3>
                <p className="text-gray-600 mb-4">
                    Expert handling of all work permit categories and classes.
                </p>
                <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Class A, B, C, D, G, H, I permits</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Initial applications and renewals</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Permit transfers and amendments</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Compliance documentation</span>
                    </li>
                </ul>
                </div>

                {/* Special Passes */}
                <div className="group bg-gradient-to-br from-white to-purple-50 border-2 border-purple-100 rounded-2xl p-6 hover:border-purple-300 hover:shadow-xl transition-all duration-300">
                <div className="bg-purple-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <GraduationCap className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Special Passes
                </h3>
                <p className="text-gray-600 mb-4">
                    Supporting families and individuals with specialized pass requirements.
                </p>
                <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Dependent passes for spouses and children</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Student passes and educational visas</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Missionary and volunteer passes</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Special passes for unique circumstances</span>
                    </li>
                </ul>
                </div>

                {/* Residency & Citizenship */}
                <div className="group bg-gradient-to-br from-white to-green-50 border-2 border-green-100 rounded-2xl p-6 hover:border-green-300 hover:shadow-xl transition-all duration-300">
                <div className="bg-green-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Home className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Residency & Citizenship
                </h3>
                <p className="text-gray-600 mb-4">
                    Long-term immigration solutions for permanent settlement.
                </p>
                <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Permanent residency applications</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Citizenship by investment guidance</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Naturalization process support</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Documentation and eligibility assessment</span>
                    </li>
                </ul>
                </div>

                {/* Corporate Support */}
                <div className="group bg-gradient-to-br from-white to-cyan-50 border-2 border-cyan-100 rounded-2xl p-6 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 md:col-span-2">
                <div className="bg-cyan-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Building2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Corporate Immigration Compliance
                </h3>
                <p className="text-gray-600 mb-4">
                    Dedicated support for businesses and organizations managing international staff.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Bulk work permit processing for multiple employees</span>
                    </li>
                    <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Immigration policy development</span>
                    </li>
                    <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Compliance audits and reporting</span>
                    </li>
                    </ul>
                    <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>NGO and diplomatic mission support</span>
                    </li>
                    <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Liaison with immigration authorities</span>
                    </li>
                    <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Employee onboarding immigration support</span>
                    </li>
                    </ul>
                </div>
                </div>
            </div>
            </div>
        </section>

        {/* Our Process */}
        <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
            <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">Our Streamlined Process</h2>
                <p className="text-xl text-slate-300">From consultation to approval, we're with you every step</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
                {[
                { number: 1, title: 'Consultation', desc: 'We assess your needs and provide expert guidance on requirements', icon: Users },
                { number: 2, title: 'Documentation', desc: 'We prepare and verify all required documents for submission', icon: FileText },
                { number: 3, title: 'Submission', desc: 'We file applications and liaise with immigration authorities', icon: Shield },
                { number: 4, title: 'Follow-up', desc: 'We track progress and handle any queries until approval', icon: Clock },
                ].map((step) => (
                <div key={step.number} className="text-center group">
                    <div className="relative mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 transition-transform duration-300">
                        <step.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 bg-white text-blue-600 w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-lg">
                        {step.number}
                    </div>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-slate-300">
                    {step.desc}
                    </p>
                </div>
                ))}
            </div>
            </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Northstart Systems?</h2>
                <p className="text-xl text-gray-600">Excellence in immigration services</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Clock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Fast Processing</h3>
                <p className="text-gray-600 leading-relaxed">
                    We minimize delays with efficient document handling and proactive follow-up
                </p>
                </div>

                <div className="text-center p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Compliance Assured</h3>
                <p className="text-gray-600 leading-relaxed">
                    Full adherence to Kenya's immigration laws and regulations
                </p>
                </div>

                <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Professional Service</h3>
                <p className="text-gray-600 leading-relaxed">
                    Experienced team providing personalized, end-to-end support
                </p>
                </div>
            </div>
            </div>
        </section>

        {/* Who We Serve */}
        <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Who We Serve</h2>
                <p className="text-xl text-gray-600">Supporting individuals and organizations alike</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border-2 border-blue-100">
                <div className="flex items-center mb-6">
                    <div className="bg-blue-100 p-3 rounded-xl mr-4">
                    <Users className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Individuals</h3>
                </div>
                <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Foreign nationals seeking to work in Kenya</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Families relocating with dependents</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Students pursuing education in Kenya</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Investors and entrepreneurs</span>
                    </li>
                </ul>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border-2 border-indigo-100">
                <div className="flex items-center mb-6">
                    <div className="bg-indigo-100 p-3 rounded-xl mr-4">
                    <Building2 className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Organizations</h3>
                </div>
                <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Multinational corporations with local operations</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>NGOs bringing in international staff</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Diplomatic missions and embassies</span>
                    </li>
                    <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span>Educational institutions with foreign faculty</span>
                    </li>
                </ul>
                </div>
            </div>
            </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
            <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 mb-10 leading-relaxed">
                Let us handle your immigration needs with professionalism and care. Our team is ready to assist you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl"
                >
                Contact Us Today
                <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <button className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white rounded-xl font-bold hover:bg-white/10 transition-all duration-300 border-2 border-white">
                Schedule Consultation
                </button>
            </div>
            </div>
        </section>
        </div>
    </div>
  );
}